import api from '../client';

/**
 * Service pour la gestion des paiements (Stripe)
 */
const paymentService = {
  /**
   * Créer un Payment Intent
   * @param {string} orderId - ID de la commande
   * @returns {Promise} Client secret et détails du paiement
   */
  createPaymentIntent: async (orderId) => {
    try {
      const response = await api.post('/payments/intent', { orderId });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Créer une session Checkout Stripe
   * @param {Object} checkoutData - Données (orderId, successUrl, cancelUrl)
   * @returns {Promise} Session ID et URL de redirection
   */
  createCheckoutSession: async (checkoutData) => {
    try {
      const response = await api.post('/payments/checkout', checkoutData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Confirmer un paiement
   * @param {string} paymentIntentId - ID du Payment Intent
   * @returns {Promise} Confirmation du paiement
   */
  confirmPayment: async (paymentIntentId) => {
    try {
      const response = await api.post('/payments/confirm', { paymentIntentId });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Créer un remboursement
   * @param {string} paymentIntentId - ID du Payment Intent
   * @param {number} amount - Montant à rembourser (optionnel, remboursement complet par défaut)
   * @returns {Promise} Détails du remboursement
   */
  createRefund: async (paymentIntentId, amount = null) => {
    try {
      const response = await api.post('/payments/refund', { 
        paymentIntentId,
        ...(amount && { amount })
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Récupérer l'historique des paiements
   * @param {Object} params - Paramètres (page, limit, status)
   * @returns {Promise} Liste des paiements
   */
  getPaymentHistory: async (params = {}) => {
    try {
      const response = await api.get('/payments', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Récupérer un paiement par son ID
   * @param {string} id - ID du paiement
   * @returns {Promise} Détails du paiement
   */
  getPaymentById: async (id) => {
    try {
      const response = await api.get(`/payments/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  /**
   * Récupérer le flux des transactions caisse (SMS Mobile Money interceptés).
   * @param {Object} params - Filtres : status ('Disponible'|'Utilisée'),
   *   operator ('MTN'|'ORANGE'), limit (1–200), skip (≥0)
   * @returns {Promise} { success, data: [...], meta: { total, limit, skip } }
   */
  getCashTransactions: async (params = {}) => {
    try {
      const response = await api.get('/payments/cash-transactions', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
};

export default paymentService;
